import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { getOutliersDesviacionFacturas } from "../../helpers/backend_helper";
import WidgetLoader from "./WidgetLoader";

const ModalOutliersDesviacionProveedor = ({ isOpen, toggle, proveedor, fechaInicio, fechaFin, aseguradora }) => {
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [facturasOutliers, setFacturasOutliers] = useState({ agudas: [], cronicas: [], estadisticas: {} });
  const [tabActiva, setTabActiva] = useState("agudas");

  useEffect(() => {
    if (isOpen && proveedor) {
      cargarFacturasOutliers();
    }
  }, [isOpen, proveedor, fechaInicio, fechaFin, aseguradora]);

  const cargarFacturasOutliers = async () => {
    setCargando(true);
    setError(null);

    try {
      const response = await getOutliersDesviacionFacturas(proveedor.codigo, {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        aseguradora: aseguradora
      });
      
      setFacturasOutliers(response.data?.data || { agudas: [], cronicas: [], estadisticas: {} });
    } catch (err) {
      console.error("Error al cargar facturas outliers:", err);
      setError(err.message || "Error al cargar las facturas outliers");
    } finally {
      setCargando(false);
    }
  };

  const formatearNumero = (numero, decimales = 2) => {
    if (!numero && numero !== 0) return "0.00";
    return Number(numero).toLocaleString("es-ES", {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "-";
    const date = new Date(fecha);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const renderTablaFacturas = (facturas, tipo) => {
    if (facturas.length === 0) {
      return (
        <Alert color="info" className="text-center">
          <i className="ri-information-line me-2"></i>
          No hay facturas {tipo} con valores anómalos detectados por desviación estándar
        </Alert>
      );
    }

    return (
      <div className="table-responsive">
        <Table className="table-hover table-sm align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>Nº Factura</th>
              <th>Fecha</th>
              <th className="text-end">Monto USD</th>
              <th className="text-center">Tipo</th>
              <th>Observación</th>
            </tr>
          </thead>
          <tbody>
            {facturas.map((factura, index) => (
              <tr key={index}>
                <td>
                  {factura.solicitud_id ? (
                    <Link to={`/solicitud/ver/${factura.solicitud_id}`} target="_blank" rel="noopener noreferrer" className="fw-medium text-primary text-decoration-underline">
                      {factura.numero_factura} <i className="ri-external-link-line ms-1 fs-11"></i>
                    </Link>
                  ) : (
                    <span className="fw-medium">{factura.numero_factura}</span>
                  )}
                </td>
                <td>
                  <small className="text-muted">{formatearFecha(factura.fecha)}</small>
                </td>
                <td className="text-end">
                  <span className={`fw-medium ${factura.es_alto ? 'text-danger' : 'text-warning'}`}>
                    $ {formatearNumero(factura.monto_usd)}
                  </span>
                </td>
                <td className="text-center">
                  <Badge color={factura.es_alto ? "danger" : "warning"} className="fs-11">
                    {factura.es_alto ? (
                      <><i className="ri-arrow-up-line"></i> Alto</>
                    ) : (
                      <><i className="ri-arrow-down-line"></i> Bajo</>
                    )}
                  </Badge>
                </td>
                <td>
                  <small className="text-muted">{factura.observacion}</small>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const estadisticasAgudas = facturasOutliers.estadisticas?.agudas || {};
  const estadisticasCronicas = facturasOutliers.estadisticas?.cronicas || {};

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle} className="bg-primary-subtle">
        <div className="d-flex align-items-center">
          <i className="ri-line-chart-line fs-20 text-primary me-2"></i>
          <div>
            <h5 className="modal-title mb-0">
              Facturas Anómalas - Desviación Estándar
            </h5>
            <small className="text-muted">
              {proveedor?.nombre} | {fechaInicio} - {fechaFin}
            </small>
          </div>
        </div>
      </ModalHeader>
      
      <ModalBody>
        {cargando ? (
          <WidgetLoader message="Cargando facturas con desviación..." />
        ) : error ? (
          <Alert color="danger">
            <i className="ri-error-warning-line me-2"></i>
            {error}
          </Alert>
        ) : (
          <>
            {/* Estadísticas Generales */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <div className="card border-0 bg-danger-subtle">
                  <div className="card-body">
                    <h6 className="text-danger mb-3">
                      <i className="ri-heart-pulse-line me-1"></i> Agudas
                    </h6>
                    <div className="row g-2">
                      <div className="col-6">
                        <small className="text-muted d-block">Media (μ)</small>
                        <strong className="text-danger">$ {formatearNumero(estadisticasAgudas.media)}</strong>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">Desv. Std (σ)</small>
                        <strong className="text-danger">$ {formatearNumero(estadisticasAgudas.desviacion)}</strong>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">Límite Inferior</small>
                        <strong className="text-danger">$ {formatearNumero(estadisticasAgudas.limite_inferior)}</strong>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">Límite Superior</small>
                        <strong className="text-danger">$ {formatearNumero(estadisticasAgudas.limite_superior)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 bg-warning-subtle">
                  <div className="card-body">
                    <h6 className="text-warning mb-3">
                      <i className="ri-medicine-bottle-line me-1"></i> Crónicas
                    </h6>
                    <div className="row g-2">
                      <div className="col-6">
                        <small className="text-muted d-block">Media (μ)</small>
                        <strong className="text-warning">$ {formatearNumero(estadisticasCronicas.media)}</strong>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">Desv. Std (σ)</small>
                        <strong className="text-warning">$ {formatearNumero(estadisticasCronicas.desviacion)}</strong>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">Límite Inferior</small>
                        <strong className="text-warning">$ {formatearNumero(estadisticasCronicas.limite_inferior)}</strong>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">Límite Superior</small>
                        <strong className="text-warning">$ {formatearNumero(estadisticasCronicas.limite_superior)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Nav tabs className="nav-tabs-custom nav-success mb-3">
              <NavItem>
                <NavLink
                  className={tabActiva === "agudas" ? "active" : ""}
                  onClick={() => setTabActiva("agudas")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-heart-pulse-line me-1"></i>
                  Agudas
                  {facturasOutliers.agudas?.length > 0 && (
                    <Badge color="danger" className="ms-2">
                      {facturasOutliers.agudas.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={tabActiva === "cronicas" ? "active" : ""}
                  onClick={() => setTabActiva("cronicas")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-medicine-bottle-line me-1"></i>
                  Crónicas
                  {facturasOutliers.cronicas?.length > 0 && (
                    <Badge color="warning" className="ms-2">
                      {facturasOutliers.cronicas.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={tabActiva}>
              <TabPane tabId="agudas">
                {renderTablaFacturas(facturasOutliers.agudas || [], "agudas")}
              </TabPane>
              <TabPane tabId="cronicas">
                {renderTablaFacturas(facturasOutliers.cronicas || [], "crónicas")}
              </TabPane>
            </TabContent>
          </>
        )}
      </ModalBody>
      
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          <i className="ri-close-line me-1"></i>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalOutliersDesviacionProveedor;
